import React, { useEffect, useState, useCallback } from 'react';
import { MessageCircle, Send, Lock, Loader2, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { getOfferThread, postOfferReply } from '../services/api';

/**
 * "Ask a Question" thread — a single bid conversation between customer and
 * driver. Used in:
 *   - Customer "Select a Driver & Pay" modal (one panel per offer)
 *   - Driver "My Offers" cards
 *   - Driver "Questions" tab
 *
 * Props:
 *   - offerId         (required) the bid this thread belongs to
 *   - role            "customer" | "driver"  who's viewing
 *   - initialBid      driver's original bid message (shown at top of thread)
 *   - defaultOpen     auto-expand on mount
 *   - onMessageSent   callback after a successful reply (parent can refresh)
 */
export default function AskAQuestion({ offerId, role, initialBid = '', defaultOpen = false, onMessageSent }) {
  const [open, setOpen] = useState(defaultOpen);
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  const fetchThread = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getOfferThread(offerId);
      setThread(data);
    } catch (e) {
      // Silent — keep thread null; UI will fall back to "couldn't load".
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    if (open && !thread) fetchThread();
  }, [open, thread, fetchThread]);

  const handleSend = async () => {
    const value = (text || '').trim();
    if (!value) return;
    setSending(true);
    try {
      await postOfferReply(offerId, value);
      setText('');
      await fetchThread();
      if (typeof onMessageSent === 'function') onMessageSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send your message.');
    } finally {
      setSending(false);
    }
  };

  const myCount = thread
    ? (role === 'customer' ? thread.customer_message_count : thread.driver_message_count)
    : 0;
  const myLimit = thread?.message_limit_per_side ?? 3;
  const canSend = thread
    ? (role === 'customer' ? thread.can_customer_reply : thread.can_driver_reply)
    : true;
  const maxLen = thread?.max_message_length ?? 250;
  const postPayment = !!thread?.post_payment;
  const remainingChars = maxLen - text.length;
  const tooLong = text.length > maxLen;

  // Compact preview line shown when the section is collapsed.
  const totalMessages = (thread?.messages?.length ?? 0) + (initialBid ? 1 : 0);
  const previewLine = totalMessages > 0
    ? `${totalMessages} message${totalMessages === 1 ? '' : 's'}`
    : 'Ask a question';

  return (
    <div className="rounded-md border border-gray-200 overflow-hidden" data-testid="ask-a-question">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors"
        data-testid="ask-a-question-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <span aria-hidden="true">💬</span>
          <MessageCircle className="w-4 h-4 text-[#1B5E20]" />
          Ask a Question
          <span className="text-xs font-medium text-gray-400">· {previewLine}</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="bg-white">
          {/* Safety notice (pre-payment only) */}
          {!postPayment && (
            <div className="flex items-start gap-2 text-[11px] text-gray-500 px-3 py-2 bg-emerald-50/40 border-b border-emerald-100">
              <ShieldAlert className="w-3.5 h-3.5 text-[#1B5E20] flex-shrink-0 mt-0.5" />
              <span>
                Messages may be monitored by JustDeliver for safety. Negotiate
                price freely — contact details, payment apps, and links to
                other chat apps cannot be shared until a booking is confirmed.
              </span>
            </div>
          )}

          {/* Thread */}
          <div className="px-3 py-3 space-y-2 max-h-72 overflow-y-auto bg-white">
            {loading && !thread ? (
              <div className="flex items-center justify-center py-4 text-xs text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Loading…
              </div>
            ) : (
              <>
                {/* Initial driver bid message */}
                {initialBid && (
                  <MessageBubble
                    side={role === 'driver' ? 'right' : 'left'}
                    senderRole="driver"
                    senderName={thread?.driver_name || 'Driver'}
                    text={initialBid}
                    badge="Bid message"
                  />
                )}
                {(thread?.messages || []).map((m, i) => {
                  // System messages (bid_updated etc.) render as a neutral
                  // centered pill so they don't compete with chat bubbles.
                  if (m.sender_role === 'system') {
                    return (
                      <div key={i} className="flex justify-center my-1">
                        <div className="px-2.5 py-1 rounded-full bg-amber-100/80 border border-amber-200 text-amber-900 text-[11px] font-semibold text-center max-w-[90%]">
                          <span className="mr-1" aria-hidden>💬</span>{m.text}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <MessageBubble
                      key={i}
                      side={m.sender_role === role ? 'right' : 'left'}
                      senderRole={m.sender_role}
                      senderName={m.sender_name}
                      text={m.text}
                    />
                  );
                })}
                {!initialBid && (thread?.messages || []).length === 0 && (
                  <p className="text-xs text-gray-400 italic text-center py-2">
                    {role === 'driver'
                      ? 'No messages yet — your bid message will appear here.'
                      : 'No messages yet — be the first to ask.'}
                  </p>
                )}
              </>
            )}
          </div>

          {/* Composer or lock */}
          <div className="border-t border-gray-100 bg-gray-50/50 px-3 py-2.5">
            {canSend ? (
              <div className="space-y-1.5">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value.slice(0, maxLen))}
                  placeholder={
                    role === 'driver'
                      ? 'Ask a question or negotiate price (no contact details)…'
                      : 'Ask a question or negotiate price (no contact details)…'
                  }
                  rows={2}
                  maxLength={maxLen}
                  className={`w-full rounded-md border text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#2E7D32]/30 ${tooLong ? 'border-red-300 focus:ring-red-300' : 'border-gray-200 focus:border-[#2E7D32]'}`}
                  data-testid="ask-a-question-input"
                />
                <div className="flex items-center justify-between gap-2">
                  <span
                    className={`text-[11px] tabular-nums ${remainingChars < 0 ? 'text-red-600 font-medium' : 'text-gray-400'}`}
                    data-testid="ask-a-question-counter"
                  >
                    {text.length} / {maxLen}
                    {!postPayment && myLimit > 0 && (
                      <span
                        className={`ml-2 font-semibold ${(myLimit - myCount) === 1 ? 'text-amber-700' : 'text-gray-500'}`}
                      >
                        {Math.max(0, myLimit - myCount)} / {myLimit} messages left
                      </span>
                    )}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSend}
                    disabled={sending || !text.trim() || tooLong}
                    className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-md h-8 px-3 gap-1.5"
                    data-testid="ask-a-question-send"
                  >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Send
                  </Button>
                </div>
                {!postPayment && myLimit > 0 && (myLimit - myCount) === 1 && (
                  <p className="text-[11px] text-amber-700 font-medium" data-testid="ask-a-question-one-left">
                    You have 1 message remaining. After this, the chat will lock
                    {role === 'customer' ? ' until you book this driver.' : ' until the customer chooses you.'}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2 text-xs text-gray-700 py-1.5 px-2 bg-gray-100/60 rounded" data-testid="ask-a-question-locked">
                <Lock className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">You have used all {myLimit} messages.</p>
                  <p className="text-gray-600">
                    {role === 'customer'
                      ? 'Proceed with the booking to continue chatting with this driver.'
                      : 'Wait for the customer to choose your bid to continue chatting.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MessageBubble({ side, senderRole, senderName, text, badge }) {
  const isRight = side === 'right';
  const themeBg =
    senderRole === 'driver'
      ? 'bg-emerald-50 border-emerald-100 text-gray-800'
      : 'bg-blue-50 border-blue-100 text-gray-800';
  return (
    <div className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] px-3 py-2 rounded-lg border text-sm ${themeBg}`}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[11px] font-semibold text-gray-600">{senderName}</span>
          {badge && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-500">
              {badge}
            </span>
          )}
        </div>
        <p className="whitespace-pre-wrap break-words leading-snug">{text}</p>
      </div>
    </div>
  );
}
